import { ArrowDown, ArrowUp } from 'lucide-react';
import { useEffect, useState } from 'react';

import { smoothScrollTo } from '@/shared/lib/smoothScroll';

/**
 * Below this many pixels from an edge, the page counts as being at it.
 *
 * <p>Not zero. A document whose height is a fraction of a pixel off — which is ordinary,
 * because borders and line heights are not integers — never reports `scrollY + innerHeight`
 * as exactly `scrollHeight`, so an exact test leaves the down arrow showing at the bottom of
 * every page, pointing at nothing.
 */
const EDGE_SLACK_PX = 24;

/**
 * Jump to the top or the bottom of the page, from the corner.
 *
 * <h2>Mounted once, for every screen</h2>
 *
 * In `AppLayout` rather than on the screen that asked for it. "Take me back to the top" is
 * not a Team idea — it belongs to any page long enough to lose your place in, and the Team
 * list is only the first of those. Put on one screen it would be rebuilt on the next, and the
 * two copies would disagree about the size of the buttons, the easing, or which edge counts
 * as the bottom.
 *
 * <h2>Each button appears only when it can do something</h2>
 *
 * The up arrow once there is something above; the down arrow while there is something below.
 * A control that is always there and does nothing on half the pages it appears on is a
 * control people stop reaching for — and on a screen that does not scroll at all, neither is
 * rendered, so a short page carries no furniture it has no use for.
 *
 * <p>They fade rather than appear, because something materialising under the pointer at the
 * moment somebody starts scrolling is read as a misclick waiting to happen.
 *
 * <h2>Measured on scroll and on resize, and coalesced to one frame</h2>
 *
 * Scroll fires far faster than the screen redraws, and reading `scrollHeight` forces the
 * browser to lay the page out. Without the frame guard this measures dozens of times per
 * scroll and each one is a layout — the pattern `SectionLoader` already uses for the same
 * reason.
 *
 * <p>Resize matters as much as scroll and is the one usually left out: a page that needed the
 * down arrow at 800px tall may not at 1400px, and rotating a phone changes it both ways.
 *
 * <h2>The scroll is the application's, not the browser's</h2>
 *
 * `smoothScrollTo` — the same half-second ease-in-out the form errors and the added-member
 * banner use, so the whole application moves at one speed. `scrollTo({ behavior: 'smooth' })`
 * would be shorter and gives no control over the duration; Chrome's is fast enough that a
 * jump from the bottom of a hundred rows reads as a teleport, which is how it was reported
 * the first time. It also already turns into an instant move for a reader who has asked for
 * less motion.
 */
export function PageScrollButtons() {
  const [canGoUp, setCanGoUp] = useState(false);
  const [canGoDown, setCanGoDown] = useState(false);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;

      const viewport = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const offset = window.scrollY;

      // Nothing to scroll at all: both stay hidden rather than one of them sitting there
      // disabled. `EDGE_SLACK_PX` keeps a page a hair taller than the window from counting.
      const scrollable = documentHeight - viewport > EDGE_SLACK_PX;

      setCanGoUp(scrollable && offset > EDGE_SLACK_PX);
      setCanGoDown(scrollable && offset + viewport < documentHeight - EDGE_SLACK_PX);
    };

    const schedule = () => {
      frame ||= requestAnimationFrame(measure);
    };

    measure();

    // Capturing, so a scroll inside any container between here and the document is seen.
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);

    /*
     * The page's own height changes without a scroll or a resize — a table swapping ten rows
     * for a hundred, a banner being dismissed, a lazy route arriving. Without this the down
     * arrow stays hidden after the rows that made it necessary have loaded, which is exactly
     * the case it exists for.
     */
    const observer = new ResizeObserver(schedule);
    observer.observe(document.documentElement);

    return () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, []);

  if (!canGoUp && !canGoDown) {
    return null;
  }

  return (
    /*
     * Bottom right, above everything. Nothing else in the application is anchored to this
     * corner -- the only other fixed element is the skip link, top left -- and `z-40` keeps
     * these under the `z-50` popovers and menus, which must not be covered by decoration.
     *
     * `pointer-events-none` on the column and `auto` on the buttons: the gap between them
     * would otherwise be an invisible strip swallowing clicks on whatever is underneath.
     */
    <div className="pointer-events-none fixed right-4 bottom-4 z-40 flex flex-col gap-2 sm:right-6 sm:bottom-6">
      {canGoUp ? (
        <ScrollButton
          label="Scroll to top"
          onClick={() => {
            smoothScrollTo(0);
          }}
        >
          <ArrowUp aria-hidden className="h-5 w-5" />
        </ScrollButton>
      ) : null}

      {canGoDown ? (
        <ScrollButton
          label="Scroll to bottom"
          onClick={() => {
            smoothScrollTo(document.documentElement.scrollHeight);
          }}
        >
          <ArrowDown aria-hidden className="h-5 w-5" />
        </ScrollButton>
      ) : null}
    </div>
  );
}

interface ScrollButtonProps {
  /** The accessible name. The button's only child is a decorative glyph. */
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}

/**
 * One round button, so the two cannot drift apart in size, colour or focus treatment.
 *
 * <p>`animate-in fade-in` rather than a bare appearance: see the class comment.
 *
 * <h2>`app-outline-button` is not decoration</h2>
 *
 * It is one of the dozen classes the ported skin's `.dark button:not(…)` rules opt out of.
 * Without it, every `dark:` colour below is repainted `var(--secondary-foreground)` and
 * `var(--accent)` on hover, at sixteen classes' specificity plus `!important` — which
 * nothing written here can outrank. `ComingSoonGate` carries the same
 * class for the same reason; it is the escape hatch, not a style.
 */
function ScrollButton({ label, onClick, children }: ScrollButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="app-outline-button animate-in fade-in pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-lg transition-colors duration-200 hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:border-white/10 dark:bg-[#0f1e3d] dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
    >
      {children}
    </button>
  );
}
